/**
=========================================================
* BASE: Learning Repository - v1.0.0
=========================================================

* Copyright 2022 Sean Dooley (https://www.dooley-dev.com)

Coded by www.dooley-dev.com

 =========================================================

* The above copyright notice shall be included in substantial portions of the Software.
*/

// React
import React from 'react';
// redux hooks
import { useFieldArray, Controller } from 'react-hook-form';
// BASE components
import BASESvgIcon from '../../../../../../../@BASE/core/BASESvgIcon';
import BASEUtils from '../../../../../../../@BASE/utils/BASEUtils';
// material-ui components
import Accordion from '@mui/material/Accordion';
import AccordionActions from '@mui/material/AccordionActions';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/system/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import IconButton from '@mui/material/IconButton';

import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Timeline from '@mui/lab/Timeline';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineItem, { timelineItemClasses } from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import Typography from '@mui/material/Typography';
// custom components
import NumberedHeading from '../../../../../../shared-components/forms/NumberedHeading';
import LureErrorIdentificationFeedbackArray from './LureErrorIdentificationFeedbackArray';
import QuestionMaterialsArray from '../../shared/QuestionMaterialsArray';
import WYSIWYGEditor from '../../../../../../shared-components/WYSIWYGEditor';
import { ReviewData } from '../../../data/Defaults';

export default function ErrorIdentificationFeedbackArray({
  control,
  register,
  setValue,
  getValues,
  errors,
  inputDir,
  materialPages
}) {
  // setup field array
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: 'content.core'
  });

  return (
    <>
      <Timeline
        sx={{
          [`& .${timelineItemClasses.root}:before`]: {
            flex: 0,
            padding: 0
          }
        }}
        className="px-0"
      >
        {fields.map((item, index) => {
          return (
            <TimelineItem key={item.id}>
              <TimelineSeparator>
                <TimelineDot color="secondary" sx={{ marginTop: '22px' }} />
                <TimelineConnector />
              </TimelineSeparator>
              <TimelineContent className="py-0">
                <Accordion
                  defaultExpanded
                  className="bg-transparent shadow-none"
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls={`${index}`}
                    id={`${index}`}
                    className="py-0 px-16 m-0 rounded bg-white shadow-sm"
                    sx={{
                      border: 'solid 1px',
                      borderColor: '#bdbdbd'
                    }}
                  >
                    <Typography className="grow m-0 font-semibold">
                      Question {index + 1}
                    </Typography>
                    {index !== 0 && (
                      <IconButton
                        className="ml-16 mr-8"
                        aria-label="move up"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          move(index, index - 1);
                        }}
                      >
                        <BASESvgIcon size={20}>
                          heroicons-solid:arrow-circle-up
                        </BASESvgIcon>
                      </IconButton>
                    )}
                    {index !== fields.length - 1 && (
                      <IconButton
                        className="mx-8"
                        aria-label="move down"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          move(index, index + 1);
                        }}
                      >
                        <BASESvgIcon size={20}>
                          heroicons-solid:arrow-circle-down
                        </BASESvgIcon>
                      </IconButton>
                    )}
                    <IconButton
                      className="ml-8 mr-16"
                      aria-label="delete"
                      color="error"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        remove(index);
                      }}
                    >
                      <BASESvgIcon size={20}>
                        heroicons-outline:trash
                      </BASESvgIcon>
                    </IconButton>
                  </AccordionSummary>
                  <AccordionDetails className="p-0">
                    <Box className="p-0">
                      <NumberedHeading
                        number="1"
                        title="Materials"
                        instructions="Using the controls below, please add materials for the question."
                      />
                      <Controller
                        control={control}
                        name={`content.core.${index}.materials`}
                        render={({ field }) => (
                          <QuestionMaterialsArray
                            nestIndex={index}
                            inputDir={inputDir}
                            materialPages={materialPages}
                            {...{ control, errors }}
                          />
                        )}
                      />

                      <Divider className="mt-16" />

                      <NumberedHeading
                        number="2"
                        title="Audio Segment Time Stamps"
                        instructions="Add the trimmed timestamp of the audio segment."
                      />
                      <Grid container spacing={1}>
                        <Grid item xs={6} sm={3}>
                          <Controller
                            control={control}
                            name={`content.core.${index}.start_minute`}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                className="mt-16 mb-16"
                                label="Start Time (Minute)"
                                placeholder="Start Time (Minute)"
                                error={
                                  !!errors.content?.core?.[index]?.start_minute
                                }
                                helperText={
                                  errors.content?.core?.[index]?.start_minute
                                    ?.message
                                }
                                fullWidth
                                required
                                type="number"
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Controller
                            control={control}
                            name={`content.core.${index}.start_second`}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                className="mt-16 mb-16"
                                label="Start Time (Second)"
                                placeholder="Start Time (Second)"
                                error={
                                  !!errors.content?.core?.[index]?.start_second
                                }
                                helperText={
                                  errors.content?.core?.[index]?.start_second
                                    ?.message
                                }
                                fullWidth
                                required
                                type="number"
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Controller
                            control={control}
                            name={`content.core.${index}.end_minute`}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                className="mt-16 mb-16"
                                label="End Time (Minute)"
                                placeholder="End Time (Minute)"
                                error={
                                  !!errors.content?.core?.[index]?.end_minute
                                }
                                helperText={
                                  errors.content?.core?.[index]?.end_minute
                                    ?.message
                                }
                                fullWidth
                                required
                                type="number"
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Controller
                            control={control}
                            name={`content.core.${index}.end_second`}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                className="mt-16 mb-16"
                                label="End Time (Second)"
                                placeholder="End Time (Second)"
                                error={
                                  !!errors.content?.core?.[index]?.end_second
                                }
                                helperText={
                                  errors.content?.core?.[index]?.end_second
                                    ?.message
                                }
                                fullWidth
                                required
                                type="number"
                              />
                            )}
                          />
                        </Grid>
                      </Grid>

                      <Divider className="mt-16" />

                      <NumberedHeading
                        number="3"
                        title="Correct Authentic Transcript"
                        instructions="Please provide the text for the authentic transcript with segments higlighted. Use yellow for any clickable word/phrase that is not needing to be changed in the text and green for any word/phrase that is incorrect but will be swapped with a word from the list of lures below. Please be gognizant of order found in text vs order in list"
                      />
                      <Controller
                        control={control}
                        name={`content.core.${index}.authentic_transcript_correct`}
                        render={({ field }) => (
                          <WYSIWYGEditor
                            {...field}
                            allowGreen
                            error={
                              errors.content?.core?.[index]
                                ?.authentic_transcript_correct
                            }
                          />
                        )}
                      />

                      <Divider className="mt-16" />

                      <NumberedHeading
                        number="4"
                        title="Inaccurate Authentic Transcript"
                        instructions="Please provide the text for the authentic transcript using inaccurate words/phrases to be for ease of comparison and review."
                      />
                      <Controller
                        control={control}
                        name={`content.core.${index}.authentic_transcript_inaccurate`}
                        render={({ field }) => (
                          <WYSIWYGEditor
                            {...field}
                            error={
                              errors.content?.core?.[index]
                                ?.authentic_transcript_inaccurate
                            }
                          />
                        )}
                      />

                      <Divider className="mt-16" />

                      <NumberedHeading
                        number="5"
                        title="Lures (Errors)"
                        instructions="Using the controls below, please add lures for the question. Be mindful of order added and order found in the content above."
                      />
                      <Controller
                        control={control}
                        name={`content.core.${index}.lures`}
                        render={({ field }) => (
                          <LureErrorIdentificationFeedbackArray
                            nestIndex={index}
                            inputDir={inputDir}
                            {...{ control, errors }}
                          />
                        )}
                      />

                      <Divider className="mt-16" />

                      <NumberedHeading
                        number="6"
                        title="Correct Feedback"
                        instructions="Enter the feedback for a correct response"
                      />
                      <Controller
                        control={control}
                        name={`content.core.${index}.correct_feedback`}
                        render={({ field }) => (
                          <WYSIWYGEditor
                            {...field}
                            error={
                              errors.content?.core?.[index]?.correct_feedback
                            }
                          />
                        )}
                      />

                      <Divider className="mt-16" />

                      <NumberedHeading
                        number="7"
                        title="First Incorrect Feedback"
                        instructions="Enter the feedback for the first incorrect response."
                      />
                      <Controller
                        control={control}
                        name={`content.core.${index}.incorrect_feedback_1`}
                        render={({ field }) => (
                          <WYSIWYGEditor
                            {...field}
                            error={
                              errors.content?.core?.[index]
                                ?.incorrect_feedback_1
                            }
                          />
                        )}
                      />

                      <Divider className="mt-16" />

                      <NumberedHeading
                        number="8"
                        title="Second Incorrect Feedback"
                        instructions="Enter the feedback for the second incorrect response."
                      />
                      <Controller
                        control={control}
                        name={`content.core.${index}.incorrect_feedback_2`}
                        render={({ field }) => (
                          <WYSIWYGEditor
                            {...field}
                            error={
                              errors.content?.core?.[index]
                                ?.incorrect_feedback_2
                            }
                          />
                        )}
                      />

                      <Divider className="mt-16" />

                      <NumberedHeading
                        number="9"
                        title="Third Incorrect Feedback"
                        instructions="Enter the feedback for the third incorrect response."
                      />
                      <Controller
                        control={control}
                        name={`content.core.${index}.incorrect_feedback_3`}
                        render={({ field }) => (
                          <WYSIWYGEditor
                            {...field}
                            error={
                              errors.content?.core?.[index]
                                ?.incorrect_feedback_3
                            }
                          />
                        )}
                      />

                      <Divider className="mt-16" />

                      <NumberedHeading
                        number="10"
                        title="Max Number of Tries"
                        instructions="Enter the amount of tries the learner should get."
                      />
                      <Controller
                        control={control}
                        name={`content.core.${index}.max_number_of_tries`}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            className="mt-16 mb-16"
                            label="Max Number of Tries"
                            placeholder="Max Number of Tries"
                            error={
                              !!errors.content?.core?.[index]
                                ?.max_number_of_tries
                            }
                            helperText={
                              errors.content?.core?.[index]?.max_number_of_tries
                                ?.message
                            }
                            fullWidth
                            required
                            type="number"
                          />
                        )}
                      />
                      <Divider className="mt-16" />
                    </Box>
                  </AccordionDetails>
                  <AccordionActions>
                    {index !== 0 && (
                      <Button
                        variant="outlined"
                        onClick={() => move(index, index - 1)}
                        startIcon={
                          <BASESvgIcon size={20}>
                            heroicons-solid:arrow-circle-up
                          </BASESvgIcon>
                        }
                        size="small"
                      >
                        Move Up
                      </Button>
                    )}
                    {index !== fields.length - 1 && (
                      <Button
                        variant="outlined"
                        onClick={() => move(index, index + 1)}
                        startIcon={
                          <BASESvgIcon size={20}>
                            heroicons-solid:arrow-circle-down
                          </BASESvgIcon>
                        }
                        size="small"
                      >
                        Move Down
                      </Button>
                    )}
                    <Button
                      variant="contained"
                      onClick={() => remove(index)}
                      color="error"
                      startIcon={
                        <BASESvgIcon size={20}>
                          heroicons-outline:trash
                        </BASESvgIcon>
                      }
                      size="small"
                    >
                      Remove
                    </Button>
                  </AccordionActions>
                </Accordion>
              </TimelineContent>
            </TimelineItem>
          );
        })}
      </Timeline>
      <Box className="flex items-center mt-0 p-0">
        <Button
          className="group flex items-center mt-8 rounded cursor-pointer"
          color="secondary"
          variant="contained"
          onClick={() =>
            append({
              id: BASEUtils.generateGUID(),
              review: ReviewData,
              materials: [
                {
                  material_title_en: '',
                  material_title_tl: '',
                  link: '',
                  sp_url: '',
                  media_material: false,
                  media_page_id: ''
                }
              ],
              start_minute: 0,
              start_second: 0,
              end_minute: 0,
              end_second: 0,
              authentic_transcript_correct: '',
              authentic_transcript_inaccurate: '',
              lures: [
                {
                  error: '',
                  feedback: ''
                }
              ],
              correct_feedback: '',
              incorrect_feedback_1: '',
              incorrect_feedback_2: '',
              incorrect_feedback_3: '',
              max_number_of_tries: 3
            })
          }
        >
          <BASESvgIcon size={20}>heroicons-solid:plus-circle</BASESvgIcon>
          <span className="ml-8 font-medium text-secondary group-hover:underline">
            Add question
          </span>
        </Button>
      </Box>
    </>
  );
}
